import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';

import LoadingComponent from 'components/LoadingComponent';
import { ENUM_TYPE_REPORT_STATUS, ENUM_TYPE_VIDEO } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import useChildren from 'hooks/useChildren';
import { useResize } from 'hooks/useResize';
import { calculateWidth, handleError } from 'utils/helper';

import ListTime from '../_components/ListTime';
import RecentAnswerResultsAndVideoResults from '../_components/RecentAnswerResultsAndVideoResults/RecentAnswerResultsAndVideoResults';
import RecentEvaluationAnalysisResults from '../_components/RecentEvaluationAnalysisResults/RecentEvaluationAnalysisResults';
import RecentExerciseVideoSelection from '../_components/RecentExerciseVideoSelection/RecentExerciseVideoSelection';

const INITIAL_DATA = {
  comment: '',
  scores: [],
  categories: [],
  list_dates: {
    reports: [],
    analysis: [],
  },
  status: ENUM_TYPE_REPORT_STATUS.PROCESSING,
};

const ReportList = () => {
  const { me } = useSelector((state) => state.auth);
  const { childrenDetail } = useSelector((state) => state.children);

  const { getDatesHasEvaluation, getEvaluationDetail } = useChildren();
  const { width } = useResize();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPassingData, setIsLoadingPassingData] = useState(false);
  const [listTime, setListTime] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [datesHasEvaluation, setDatesHasEvaluation] = useState([]);
  const [datesHasVideo, setDatesHasVideo] = useState([]);
  const [datesReportRegistration, setDatesReportRegistration] = useState([]);
  const [selectedVideoASE, setSelectedVideoASE] = useState([]);
  const [selectedVideoDAY, setSelectedVideoDAY] = useState([]);

  const isCallingApi = useRef(false);

  const form = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: INITIAL_DATA,
  });

  // GET DETAIL EVALUATION
  const _getDetailEvaluation = async (id) => {
    if (id) {
      try {
        const response = await getEvaluationDetail(id);

        setDatesHasVideo(response.list_dates.reports);
        setDatesReportRegistration(response.list_dates.analysis);

        const categoriesASE = response.categories
          .filter((item) => item.videocategory_type === ENUM_TYPE_VIDEO.ASE)
          .map((item) => item.videocategory_id);

        const categoriesDAY = response.categories
          .filter((item) => item.videocategory_type === ENUM_TYPE_VIDEO.DAY)
          .map((item) => item.videocategory_id);

        setSelectedVideoASE(categoriesASE);
        setSelectedVideoDAY(categoriesDAY);

        form.setValues({
          comment: response.comment,
          scores: response.scores,
          categories: [],
          list_dates: {
            reports: response.list_dates.reports,
            analysis: response.list_dates.analysis,
          },
          status: response.status,
        });
      } catch (error) {
        handleError(error);
      } finally {
        setTimeout(() => {
          setIsLoadingPassingData(false);
        }, 500);
      }
    }
  };

  const handleActiveIndex = async (index) => {
    if (index === activeIndex) return;

    setActiveIndex(index);
    setIsLoadingPassingData(true);
    await _getDetailEvaluation(datesHasEvaluation[index].id);
  };

  // GET DATES HAS EVALUATION
  const _getReportByDate = useCallback(async () => {
    if (me && childrenDetail?.id && !isCallingApi.current) {
      try {
        isCallingApi.current = true;

        const response = await getDatesHasEvaluation({ page: 1, page_size: 1000 });
        if (response) {
          setDatesHasEvaluation(response);
          setListTime(response.map((item) => dayjs(item.date).format(FORMAT_TIME.FULL_DATE_TIME)));
          if (response.length > 0) {
            _getDetailEvaluation(response[0].id);
          }
        }
      } catch (error) {
        handleError(error);
      } finally {
        setTimeout(() => {
          isCallingApi.current = false;
          setIsLoading(false);
        }, 500);
      }
    }
  }, [me, childrenDetail?.id]);

  useEffect(() => {
    _getReportByDate();
  }, [_getReportByDate]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <LoadingComponent />
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-full">
      {datesHasEvaluation.length === 0 ? (
        <div className="w-full h-full bg-white flex justify-center items-center">データがありません</div>
      ) : (
        <>
          <div className="flex gap-4 h-full w-full">
            <div style={calculateWidth(320, 1920, width)}>
              <ListTime activeIndex={activeIndex} setActiveIndex={handleActiveIndex} listTime={listTime} />
            </div>
            {isLoadingPassingData ? (
              <div className="min-w-[1424px] w-full h-full flex justify-center items-center">
                <LoadingComponent />
              </div>
            ) : (
              <>
                <div style={calculateWidth(436, 1920, width)}>
                  <RecentAnswerResultsAndVideoResults datesHasVideo={datesHasVideo} datesReportRegistration={datesReportRegistration} />{' '}
                </div>
                <div style={calculateWidth(520, 1920, width)}>
                  <RecentEvaluationAnalysisResults form={form} />
                </div>
                <div className="!w-full !max-w-full" style={calculateWidth(436, 1920, width)}>
                  {' '}
                  <RecentExerciseVideoSelection
                    selectedVideoASE={selectedVideoASE}
                    selectedVideoDAY={selectedVideoDAY}
                    setTypeVideo={() => {}}
                    handleRemoveVideo={() => {}}
                    setShowPopupAddExerciseVideo={() => {}}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportList;
